import { useState, useEffect, useCallback, useRef } from 'react'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  Upload,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useQuery } from '@tanstack/react-query'
import { previewPlanImport } from '@/api/planDetail'
import { FileUploadDropzone } from '@/components/PlanWizard/FileUploadDropzone'
import {
  IMPORT_VERDICT_ID,
  ImportResultsSummary,
} from '@/components/PlanDetail/ImportResultsSummary'
import { useHistoricalDataUpload } from '@/hooks/useHistoricalData'
import { useProductList } from '@/hooks/useProductLists'
import { useBulkImportLines } from '@/hooks/usePlanDetail'
import { toast } from '@/stores/useToastStore'
import { downloadTemplate } from '@/utils/downloadTemplate'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { cn } from '@/utils/cn'

type Step = 'upload' | 'processing' | 'preview' | 'importing'

const STEPS = ['Upload', 'Review', 'Import'] as const

function stepIndex(step: Step) {
  if (step === 'preview') return 1
  if (step === 'importing') return 2
  return 0
}

function ImportSteps({ current }: { current: number }) {
  return (
    <ol
      aria-label="Import steps"
      className="flex flex-wrap items-center gap-2 text-xs text-[#6A7282]"
    >
      {STEPS.map((label, index) => (
        <li
          key={label}
          aria-current={index === current ? 'step' : undefined}
          className={cn(
            'flex items-center gap-1.5',
            index === current && 'font-semibold text-[#1C1C1C]'
          )}
        >
          {index > 0 && (
            <span aria-hidden="true" className="mr-0.5 h-px w-6 bg-[#D6DAE1]" />
          )}
          <span
            aria-hidden="true"
            className={cn(
              'flex h-[18px] w-[18px] items-center justify-center rounded-full border text-[10px] font-semibold',
              index < current && 'border-[#6A7282] bg-[#6A7282] text-white',
              index === current &&
                'border-primary bg-primary text-white shadow-[0_0_0_3px_#FFC9CE]',
              index > current && 'border-[#D6DAE1]'
            )}
          >
            {index < current ? <Check className="h-3 w-3" /> : index + 1}
          </span>
          {label}
        </li>
      ))}
    </ol>
  )
}

interface ImportItemsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  onImportComplete: () => void
}

export function ImportItemsModal({
  open,
  onOpenChange,
  planId,
  versionId,
  onImportComplete,
}: ImportItemsModalProps) {
  const [step, setStep] = useState<Step>('upload')
  const [productListId, setProductListId] = useState<string | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const downloadController = useRef<AbortController | null>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [mismatchStatus, setMismatchStatus] = useState({
    hasMismatches: false,
    isLoading: false,
  })
  const handleMismatchStatusChange = useCallback(
    (status: { hasMismatches: boolean; isLoading: boolean }) => {
      setMismatchStatus(status)
    },
    []
  )

  const uploadState = useHistoricalDataUpload()
  const resetUpload = uploadState.reset
  const bulkImport = useBulkImportLines(planId, versionId)

  // Fetch product list details for summary stats (also fetched on failure so
  // we can surface the backend's error_message instead of a generic error)
  const {
    data: productListData,
    error: productListError,
    refetch: refetchProductList,
  } = useProductList(
    uploadState.isCompleted || uploadState.isError ? productListId : null
  )

  const matchPreview = useQuery({
    queryKey: ['planImportPreview', planId, versionId, productListId],
    queryFn: ({ signal }) =>
      previewPlanImport(planId, versionId, productListId!, true, signal),
    enabled: open && uploadState.isCompleted && !!productListId && !!versionId,
    placeholderData: (previous, query) =>
      query?.queryKey[3] === productListId ? previous : undefined,
    staleTime: 0,
  })

  // Prefer the backend's error_message (populated once the product list is
  // marked FAILED) over the generic upload/SSE error text when available.
  const processingErrorMessage = uploadState.isError
    ? (productListData?.error_message ?? uploadState.error)
    : null

  const resetBulkImport = bulkImport.reset

  const cancelDownload = useCallback(() => {
    downloadController.current?.abort()
    downloadController.current = null
    setIsDownloading(false)
  }, [])

  const resetPreparation = useCallback(() => {
    setProductListId(null)
    setPendingFile(null)
    setMismatchStatus({ hasMismatches: false, isLoading: true })
    resetUpload()
    resetBulkImport()
    setStep('upload')
  }, [resetUpload, resetBulkImport])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      cancelDownload()
      setDownloadError(null)
      resetPreparation()
    }
  }, [open, cancelDownload, resetPreparation])

  useEffect(() => () => downloadController.current?.abort(), [])

  useEffect(() => {
    if (open) {
      bodyRef.current?.scrollTo({ top: 0 })
      titleRef.current?.focus()
    }
  }, [open, step])

  const handleDownloadTemplate = async () => {
    if (downloadController.current) return
    const controller = new AbortController()
    downloadController.current = controller
    setIsDownloading(true)
    setDownloadError(null)
    try {
      await downloadTemplate({ signal: controller.signal })
    } catch {
      if (!controller.signal.aborted) {
        setDownloadError('Could not download the template. Please try again.')
      }
    } finally {
      if (downloadController.current === controller) {
        downloadController.current = null
        setIsDownloading(false)
      }
    }
  }

  // Transition to the review when processing completes
  useEffect(() => {
    if (uploadState.isCompleted && productListId) {
      setStep('preview')
    }
  }, [uploadState.isCompleted, productListId])

  // Handle file selection and upload
  const handleFileSelect = async (file: File) => {
    cancelDownload()
    setDownloadError(null)
    setProductListId(null)
    setMismatchStatus({ hasMismatches: false, isLoading: true })
    setPendingFile(file)
    try {
      setStep('processing')
      const result = await uploadState.upload(file)
      setProductListId(result.productListId)
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to upload file'))
      setStep('upload')
    }
  }

  // Handle retry after a failed upload/processing attempt
  const handleRetry = () => {
    if (pendingFile) {
      handleFileSelect(pendingFile)
    }
  }

  // Handle import confirmation
  const handleImportItems = () => {
    if (!productListId || !canImport) return

    setStep('importing')
    bulkImport.start(productListId, true)
  }

  // Handle bulk import completion or error via SSE / polling
  useEffect(() => {
    if (bulkImport.isCompleted) {
      toast.success('Items imported successfully')
      onOpenChange(false)
      onImportComplete()
    } else if (bulkImport.isError && step === 'importing') {
      toast.error(`Import failed: ${bulkImport.error}`)
      setStep('preview')
      void matchPreview.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkImport.isCompleted, bulkImport.isError])

  // Notify on processing failures detected after the upload/create call
  // resolved (e.g. reported later via SSE)
  useEffect(() => {
    if (uploadState.isError && step === 'processing') {
      toast.error(uploadState.error ?? 'Failed to process file')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadState.isError])

  // Prevent closing while actively processing or importing (still allowed
  // once processing has failed, so the user can dismiss the modal)
  const handleOpenChange = (newOpen: boolean) => {
    if (
      (step === 'processing' && !uploadState.isError) ||
      step === 'importing'
    ) {
      return
    }
    if (!newOpen) cancelDownload()
    onOpenChange(newOpen)
  }

  // Calculate progress for the processing step
  const getProgressValue = () => {
    if (uploadState.isUploading) return 25
    if (uploadState.isCreating) return 50
    if (uploadState.isProcessing) return Math.max(50, uploadState.progress)
    return uploadState.progress
  }

  const validRows = productListData?.valid_rows ?? 0
  const isMatchChecking =
    matchPreview.isFetching || matchPreview.isPlaceholderData
  const isBlocked =
    !mismatchStatus.isLoading &&
    !isMatchChecking &&
    (mismatchStatus.hasMismatches || !!matchPreview.data?.issues.length)
  const canImport =
    validRows > 0 &&
    !productListError &&
    !mismatchStatus.hasMismatches &&
    !mismatchStatus.isLoading &&
    !!matchPreview.data &&
    !isMatchChecking &&
    !matchPreview.isError &&
    matchPreview.data.issues.length === 0
  const isBusy =
    (step === 'processing' && !uploadState.isError) || step === 'importing'
  const fileName = pendingFile?.name ?? uploadState.fileName ?? ''
  const progress =
    step === 'importing' ? bulkImport.progress : getProgressValue()
  const progressLabel =
    step === 'importing'
      ? bulkImport.isStarting
        ? 'Starting import…'
        : 'Importing items…'
      : uploadState.isUploading
        ? 'Uploading file…'
        : uploadState.isCreating
          ? 'Preparing your file…'
          : 'Checking your items…'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="top-[50%] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[760px] -translate-y-1/2 gap-0 overflow-hidden rounded-2xl bg-card p-0 shadow-xl sm:rounded-2xl">
        <DialogHeader className="shrink-0 border-b px-5 pb-5 pt-6 text-left sm:px-6">
          <div className="flex items-center gap-3 pr-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent text-primary">
              <FileSpreadsheet aria-hidden="true" className="h-5 w-5" />
            </div>
            <div className="space-y-1.5">
              <DialogTitle
                ref={titleRef}
                tabIndex={-1}
                className="text-xl outline-none"
              >
                Import Items
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-5">
                {step === 'upload' &&
                  'Upload a completed template or an edited plan export. Unmatched SKUs will be added as a new item.'}
                {step === 'processing' &&
                  (uploadState.isError
                    ? 'There was an error processing your file'
                    : 'Processing your file...')}
                {step === 'preview' && 'Review the import results.'}
                {step === 'importing' && 'Importing items into your plan…'}
              </DialogDescription>
            </div>
          </div>
          <div className="mt-4">
            <ImportSteps current={stepIndex(step)} />
          </div>
        </DialogHeader>

        <div
          ref={bodyRef}
          className="min-h-0 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6"
        >
          {step === 'upload' && (
            <div className="space-y-4">
              <FileUploadDropzone
                onFileSelect={handleFileSelect}
                isLoading={false}
                error={uploadState.error}
              />
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={handleDownloadTemplate}
                disabled={isDownloading}
                aria-busy={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 aria-hidden="true" className="animate-spin" />
                ) : (
                  <Download aria-hidden="true" />
                )}
                {isDownloading
                  ? 'Preparing template…'
                  : 'Download blank template'}
              </Button>
              {downloadError && (
                <p role="alert" className="text-sm text-destructive">
                  {downloadError}
                </p>
              )}
            </div>
          )}
          {step !== 'upload' && (
            <div className="mb-5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border border-[#E5E7EB] bg-[#F8F8F8] px-3 py-2">
              <p className="flex min-w-0 items-center gap-2 text-sm font-medium">
                <FileSpreadsheet
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-[#556374]"
                />
                <span className="break-all">{fileName}</span>
                {step === 'preview' && productListData && (
                  <span className="shrink-0 font-normal text-[#6A7282]">
                    · {productListData.total_rows.toLocaleString()}{' '}
                    {productListData.total_rows === 1 ? 'row' : 'rows'}
                  </span>
                )}
              </p>
              {(step === 'preview' ||
                (step === 'processing' && uploadState.isError)) && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={resetPreparation}
                >
                  Replace file
                </Button>
              )}
            </div>
          )}

          {step === 'processing' && uploadState.isError && (
            <div className="flex flex-col items-center gap-4 rounded-xl border border-destructive/20 bg-destructive/5 px-5 py-8 text-center">
              <AlertCircle
                aria-hidden="true"
                className="h-8 w-8 text-destructive"
              />
              <div role="alert" className="space-y-2">
                <p className="text-sm font-semibold">
                  We couldn&apos;t process this file
                </p>
                <p className="break-words text-sm text-muted-foreground">
                  {processingErrorMessage}
                </p>
              </div>
              <Button variant="outline" onClick={handleRetry}>
                <RefreshCw aria-hidden="true" />
                Retry upload
              </Button>
            </div>
          )}

          {isBusy && (
            <div
              role="status"
              className="flex flex-col items-center gap-5 py-8"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                <Loader2
                  aria-hidden="true"
                  className="h-6 w-6 animate-spin text-primary"
                />
              </div>
              <div className="w-full max-w-sm space-y-3">
                <div className="flex justify-between gap-3 text-sm">
                  <span className="font-medium">{progressLabel}</span>
                  <span className="text-muted-foreground">
                    {Math.round(progress)}%
                  </span>
                </div>
                <Progress
                  value={progress}
                  aria-label={progressLabel}
                  className="h-1.5"
                />
                <p className="text-center text-xs text-muted-foreground">
                  {step === 'importing'
                    ? 'Please keep this window open while your items are imported.'
                    : 'You can review the results before making changes to your plan.'}
                </p>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <>
              {bulkImport.isError && (
                <p
                  role="alert"
                  className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0"
                  />
                  Import failed: {bulkImport.error}. You can try again.
                </p>
              )}
              {productListError ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-red-500">
                    Failed to load preview: {productListError.message}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => void refetchProductList()}
                  >
                    Try Again
                  </Button>
                </div>
              ) : productListData ? (
                <ImportResultsSummary
                  productList={productListData}
                  fileName={fileName}
                  matchCheck={{
                    data: matchPreview.isPlaceholderData
                      ? undefined
                      : matchPreview.data,
                    isChecking: isMatchChecking,
                    error: matchPreview.error,
                    retry: () => void matchPreview.refetch(),
                  }}
                  onMismatchStatusChange={handleMismatchStatusChange}
                />
              ) : (
                <div className="px-6 py-12 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-[#556374]" />
                  <span className="text-[14px] text-[#556374]">
                    Loading results...
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-row flex-wrap items-center justify-between gap-3 border-t bg-background/40 px-5 py-4 sm:justify-between sm:space-x-0 sm:px-6">
          {step === 'preview' ||
          (step === 'processing' && uploadState.isError) ? (
            <Button variant="ghost" onClick={resetPreparation} className="px-2">
              <ArrowLeft aria-hidden="true" />
              Back
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              {isBusy
                ? 'This may take a moment.'
                : 'Review your file before confirming the import.'}
            </p>
          )}
          {!isBusy && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                className="bg-card"
                onClick={() => handleOpenChange(false)}
              >
                {step === 'preview' ? 'Discard' : 'Cancel'}
              </Button>
              {step === 'preview' &&
                (isBlocked ? (
                  <Button onClick={resetPreparation}>
                    <Upload aria-hidden="true" />
                    Upload fixed file
                  </Button>
                ) : (
                  <Button
                    onClick={handleImportItems}
                    disabled={!canImport}
                    aria-describedby={IMPORT_VERDICT_ID}
                  >
                    {validRows > 0
                      ? `Import ${validRows.toLocaleString()} ${validRows === 1 ? 'item' : 'items'}`
                      : 'Import items'}
                    <ArrowRight aria-hidden="true" />
                  </Button>
                ))}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
